import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type Template, useTemplate, useUpdateTemplate } from '@/entities/template';
import { isApiError } from '@/shared/api';
import { FormError } from '@/shared/forms';
import {
  Button,
  Dialog,
  ErrorState,
  LoadingState,
  MarkdownEditor,
  TextAreaField,
  TextField,
} from '@/shared/ui';

import styles from './Templates.module.css';

/** A block of a task template: Markdown text OR a checklist (items as one-per-line text). */
type Block =
  | { key: number; kind: 'text'; text: string }
  | { key: number; kind: 'checklist'; title: string; items: string };

interface StoredBlock {
  markdown_text?: string | null;
  checklist?: { title?: string | null; items?: string[] } | null;
}

interface StoredLabel {
  name: string;
  color?: string | null;
}

let nextKey = 1;

const text = (value: unknown): string => (typeof value === 'string' ? value : '');

function blocksOf(payload: Record<string, unknown>): Block[] {
  const stored = (payload['content'] as StoredBlock[] | undefined) ?? [];
  return stored.map((block) =>
    block.checklist
      ? {
          key: nextKey++,
          kind: 'checklist',
          title: block.checklist.title ?? '',
          items: (block.checklist.items ?? []).join('\n'),
        }
      : { key: nextKey++, kind: 'text', text: block.markdown_text ?? '' },
  );
}

/** Edits the REUSABLE content of a template in place. Fields the form does not show (look, cover,
 *  label colours) are carried over from the stored payload unchanged. */
export function TemplateEditDialog({
  templateId,
  onClose,
}: {
  templateId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const query = useTemplate(templateId);
  return (
    <Dialog title={t('templates.edit.title')} onClose={onClose}>
      {query.isPending && <LoadingState />}
      {query.isError && (
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch();
          }}
        />
      )}
      {query.isSuccess && <EditForm template={query.data} onClose={onClose} />}
    </Dialog>
  );
}

function EditForm({ template, onClose }: { template: Template; onClose: () => void }) {
  const { t } = useTranslation();
  const update = useUpdateTemplate();
  const payload = template.payload;
  const isTask = template.kind === 'task';
  const [name, setName] = useState(template.name);
  const [headline, setHeadline] = useState(text(isTask ? payload['title'] : payload['name']));
  const [description, setDescription] = useState(text(payload['description']));
  const [blocks, setBlocks] = useState<Block[]>(() => (isTask ? blocksOf(payload) : []));
  const [labels, setLabels] = useState(
    ((payload['labels'] as StoredLabel[] | undefined) ?? []).map((l) => l.name).join(', '),
  );

  const nameCode = isApiError(update.error) ? update.error.fieldErrors['name']?.[0] : undefined;
  const nameError = nameCode
    ? t(`validation.${nameCode}`, { defaultValue: t('validation.INVALID') })
    : undefined;
  const invalid = name.trim() === '' || headline.trim() === '';

  const patchBlock = (key: number, change: Partial<Block>) => {
    setBlocks((all) => all.map((b) => (b.key === key ? ({ ...b, ...change } as Block) : b)));
  };
  const move = (index: number, by: -1 | 1) => {
    setBlocks((all) => {
      const next = [...all];
      const [item] = next.splice(index, 1);
      if (item) next.splice(index + by, 0, item);
      return next;
    });
  };

  const build = (): Record<string, unknown> => {
    if (!isTask) {
      return {
        ...payload,
        name: headline.trim(),
        description: description.trim() === '' ? null : description,
      };
    }
    const colours = new Map(
      ((payload['labels'] as StoredLabel[] | undefined) ?? []).map((l) => [
        l.name.toLowerCase(),
        l.color,
      ]),
    );
    const names = [
      ...new Set(
        labels
          .split(',')
          .map((x) => x.trim())
          .filter(Boolean),
      ),
    ];
    return {
      ...payload,
      title: headline.trim(),
      content: blocks.map((b) =>
        b.kind === 'text'
          ? { markdown_text: b.text }
          : {
              checklist: {
                ...(b.title.trim() && { title: b.title.trim() }),
                items: b.items
                  .split('\n')
                  .map((x) => x.trim())
                  .filter(Boolean),
              },
            },
      ),
      labels: names.map((n) => {
        const color = colours.get(n.toLowerCase());
        return color ? { name: n, color } : { name: n };
      }),
    };
  };

  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        if (invalid || update.isPending) return;
        update.mutate(
          {
            id: template.id,
            ...(name.trim() !== template.name && { name: name.trim() }),
            payload: build(),
            expected_version: template.version,
          },
          { onSuccess: onClose },
        );
      }}
    >
      <p className={styles.note}>{t(`templates.edit.note.${isTask ? 'task' : 'column'}`)}</p>
      <TextField
        label={t('templates.name')}
        value={name}
        maxLength={100}
        error={nameError}
        onChange={(event) => {
          setName(event.target.value);
        }}
      />
      <TextField
        label={t(`templates.edit.headline.${isTask ? 'task' : 'column'}`)}
        value={headline}
        maxLength={isTask ? 255 : 100}
        onChange={(event) => {
          setHeadline(event.target.value);
        }}
      />
      {!isTask && (
        <TextAreaField
          label={t('templates.edit.description')}
          value={description}
          maxLength={2000}
          rows={4}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
      )}
      {isTask && (
        <>
          <ul className={styles.blocks}>
            {blocks.map((block, index) => (
              <li key={block.key} className={styles.block}>
                {block.kind === 'text' ? (
                  <MarkdownEditor
                    label={t('templates.edit.text')}
                    value={block.text}
                    rows={5}
                    onChange={(text) => {
                      patchBlock(block.key, { text });
                    }}
                  />
                ) : (
                  <>
                    <TextField
                      label={t('templates.edit.checklistTitle')}
                      value={block.title}
                      onChange={(event) => {
                        patchBlock(block.key, { title: event.target.value });
                      }}
                    />
                    <TextAreaField
                      label={t('templates.edit.checklistItems')}
                      hint={t('templates.edit.checklistHint')}
                      value={block.items}
                      rows={4}
                      onChange={(event) => {
                        patchBlock(block.key, { items: event.target.value });
                      }}
                    />
                  </>
                )}
                <span className={styles.rowActions}>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={index === 0}
                    onClick={() => {
                      move(index, -1);
                    }}
                  >
                    {t('templates.edit.up')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={index === blocks.length - 1}
                    onClick={() => {
                      move(index, 1);
                    }}
                  >
                    {t('templates.edit.down')}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setBlocks((all) => all.filter((b) => b.key !== block.key));
                    }}
                  >
                    {t('templates.edit.remove')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
          <div className={styles.actions}>
            <Button
              size="sm"
              onClick={() => {
                setBlocks((all) => [...all, { key: nextKey++, kind: 'text', text: '' }]);
              }}
            >
              {t('templates.edit.addText')}
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setBlocks((all) => [
                  ...all,
                  { key: nextKey++, kind: 'checklist', title: '', items: '' },
                ]);
              }}
            >
              {t('templates.edit.addChecklist')}
            </Button>
          </div>
          <TextField
            label={t('templates.edit.labels')}
            hint={t('templates.edit.labelsHint')}
            value={labels}
            onChange={(event) => {
              setLabels(event.target.value);
            }}
          />
        </>
      )}
      {nameError === undefined && <FormError error={update.error} />}
      <div className={styles.actions}>
        <Button type="submit" variant="primary" disabled={invalid || update.isPending}>
          {t('common.save')}
        </Button>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
      </div>
    </form>
  );
}
